import {
  getPackageConfigurations,
  getPackageDepartures,
} from 'frontend/api/services/packageSuppliersServices';
import { createContext, useContext, useEffect, useState } from 'react';
import { PACKAGE_CARD_TABS_KEYS, PAGINATION_KEYS } from '../constants';
import { useSelector } from 'react-redux';
import ErrorSnackbar from '../components/ErrorSnackbar';

const { PAGE, PAGE_SIZE } = PAGINATION_KEYS;
const { FLIGHTS } = PACKAGE_CARD_TABS_KEYS;

const defaultSelectedTab = FLIGHTS;

export const defaultPackageCardProviderValues = {
  searching: false,
  setSearching: () => null,
  selectedTab: undefined,
  setSelectedTab: () => null,
  displayInfo: undefined,
  setDisplayInfo: () => null,
  filters: { page: { value: PAGE }, pageSize: { value: PAGE_SIZE } },
  setFilters: () => null,
  handleFilters: () => null,
  handleResetFilters: () => null,
  resetFilters: 0,
  currentHotels: undefined,
  totalPages: undefined,
  handleDisplayInfoToggle: () => null,
  handleSelectedTab: () => null,
  error: null,
  setError: () => null,
};

const PackageCardContext = createContext(defaultPackageCardProviderValues);

const PackageCardProvider = ({ children, packageData, query }) => {
  const tokenMarcaBlanca = useSelector((state) => state.applicationState.token);
  const queryParams = new URLSearchParams(query);
  const [searching, setSearching] = useState(defaultPackageCardProviderValues.searching);
  const [filters, setFilters] = useState(defaultPackageCardProviderValues.filters);
  const [resetFilters, setResetFilters] = useState(defaultPackageCardProviderValues.resetFilters);
  const [displayInfo, setDisplayInfo] = useState();
  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
  const [error, setError] = useState(null);

  // New state for departure dates
  const [departureDates, setDepartureDates] = useState(null);
  const [departureDatesIsLoading, setDepartureDatesIsLoading] = useState(false);
  const [departureDatesIsFetching, setDepartureDatesIsFetching] = useState(false);

  // New state for package configurations
  const [packageConfigurations, setPackageConfigurations] = useState(null);
  const [packageConfigurationsIsLoading, setPackageConfigurationsIsLoading] = useState(false);
  const [packageConfigurationsIsFetching, setPackageConfigurationsIsFetching] = useState(false);

  const handleDisplayInfoToggle = (value) => {
    setDisplayInfo(value !== undefined ? !!value : (state) => !state);
  };

  const handleSelectedTab = (tabKey) => {
    setSelectedTab(tabKey);
  };

  const fetchDepartureDates = async () => {
    try {
      setDepartureDatesIsFetching(true);
      setDepartureDatesIsLoading(true);
      setError(null);
      const data = await getPackageDepartures(packageData.externalId, {
        from: queryParams.get('dateSince'),
        to: queryParams.get('dateUntil'),
        occupation: queryParams.get('adultsQty'),
      });
      setDepartureDates(data);
    } catch (error) {
      console.error('Error fetching departure dates:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Error al cargar las fechas de salida';
      setError(`Error al cargar las fechas de salida: ${errorMessage}`);
    } finally {
      setDepartureDatesIsFetching(false);
      setDepartureDatesIsLoading(false);
    }
  };

  const fetchPackageConfigurations = async () => {
    if (!filters.departureDate?.value) return;

    try {
      setPackageConfigurationsIsFetching(true);
      setPackageConfigurationsIsLoading(true);
      setError(null);
      const data = await getPackageConfigurations(
        packageData.externalId,
        {
          departureDate: filters.departureDate?.value,
          occupation: queryParams.get('adultsQty'),
        },
        tokenMarcaBlanca,
      );
      setPackageConfigurations(data);
    } catch (error) {
      console.error('Error fetching package configurations:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al cargar las configuraciones del paquete';
      setError(`Error al cargar las configuraciones del paquete: ${errorMessage}`);
    } finally {
      setPackageConfigurationsIsFetching(false);
      setPackageConfigurationsIsLoading(false);
    }
  };

  const handleFilters = (key, value, valueToShow) => {
    setFilters((state) => {
      if (value) {
        return {
          ...state,
          page: { value: PAGE },
          [key]: { value, valueToShow: valueToShow || value },
        };
      } else {
        return {
          ...Object.fromEntries(Object.entries(state).filter((item) => item[0] !== key)),
          page: { value: PAGE },
        };
      }
    });
  };

  const handleResetFilters = () => setResetFilters((state) => state + 1);

  useEffect(() => {
    setFilters(defaultPackageCardProviderValues.filters); // reset filters
  }, [resetFilters]);

  useEffect(() => {
    if (displayInfo && !departureDates && !departureDatesIsFetching) {
      fetchDepartureDates();
    }
  }, [displayInfo]);

  useEffect(() => {
    if (filters.departureDate?.value) {
      fetchPackageConfigurations();
    }
  }, [filters.departureDate?.value]);

  const values = {
    searching,
    filters,
    setSearching,
    setFilters,
    handleFilters,
    handleResetFilters,
    resetFilters,
    packageConfigurations,
    packageConfigurationsIsFetching,
    packageConfigurationsIsLoading,
    departureDates,
    departureDatesIsFetching,
    departureDatesIsLoading,
    selectedTab,
    setSelectedTab,
    packageData,
    displayInfo,
    handleDisplayInfoToggle,
    handleSelectedTab,
    error,
    setError,
    fetchDepartureDates,
    fetchPackageConfigurations,
  };

  return (
    <PackageCardContext.Provider value={values}>
      {children}
      <ErrorSnackbar open={!!error} message={error} onClose={() => setError(null)} />
    </PackageCardContext.Provider>
  );
};

const usePackageCard = () => useContext(PackageCardContext);

export { PackageCardContext, PackageCardProvider, usePackageCard };
